import React from 'react';
import _ from 'underscore';
import { TextFieldWidget, CheckBoxWithLabelWidget } from '@tableau/widgets';
import { MergeOrUpdateStateAction } from '@tableau/vizportalclient-common-components';
import { ReactActionDialogBodyProps } from '../../../commonInfra/dialogs/ReactActionDialog';
import { GrantLicenseModes } from '../../../ts/ServerApi/requests/SyncActiveDirectoryGroupRequest';
import { SiteRoleOption, SiteRoleOptionsResource } from '../../services/resources/SiteRoleOptions';
import { SiteRoles } from '../../../ts/ServerApi/types/ISiteRole';
import { Formatters, Messages } from '../../../codegen/Localize';
import { ServiceLocator } from '../../../app/ServiceLocator';
import { SiteRolePickerMenu } from '../../../common/react/components/SiteRolePickerMenu';
import {
  dialogBodyStyles,
  groupNameFieldStyles,
  outerCheckboxStyles,
  textFieldStyles,
  siteRolePickerMenuStyles
} from './styles/CreateGroupDialogStyles';
import { SessionUserSelectors } from '../../../app/selectors/SessionInfoSelectors';
import { getAppStore } from '../../../app/AppStore';


const GRANT_LICENSE_ON_LOGIN_CHECKBOX_TEST_ID = 'license-upon-login';
const NAME_GROUP_TEXT_FIELD_TEST_ID = 'confirm-action-dialog-input-text-box';

const ZERO_WIDTH_SPACE = '\u200B';

export const CHARACTER_LIMIT = 255;

export type CreateGroupDialogProps = {}

export type CreateGroupDialogState = {
  groupNameErrorMessage: string;
  groupNameText: string;
  grantLicenseMode: GrantLicenseModes;
  isGroupNameTextValid: boolean;
  minimumSiteRole: string;
}

export type CreateGroupActionDialogProps = ReactActionDialogBodyProps<CreateGroupDialogProps, CreateGroupDialogState>;

export function CreateGroupDialogBody(props: CreateGroupActionDialogProps): JSX.Element {
  const {
    groupNameErrorMessage,
    groupNameText,
    grantLicenseMode,
    isGroupNameTextValid,
    minimumSiteRole
  } = props.dialogState;

  return <>
    <div style={dialogBodyStyles}>
      <div style={groupNameFieldStyles}>
        <GroupNameField
          groupNameErrorMessage={groupNameErrorMessage}
          groupNameText={groupNameText}
          isGroupNameTextValid={isGroupNameTextValid}
          doAction={props.doAction}
          updateState={props.updateDialogState}
        />
      </div>
      <div style={outerCheckboxStyles}>
        <GrantLicenseModeOnLoginCheckBox
          grantLicenseMode={grantLicenseMode}
          updateState={props.updateDialogState}
        />
      </div>
      <SiteRolePickerMenuWithInfo
        grantLicenseMode={grantLicenseMode}
        minimumSiteRole={minimumSiteRole}
        updateState={props.updateDialogState}
      />
    </div>
  </>;
}

type GroupNameFieldProps = {
  groupNameErrorMessage: string;
  groupNameText: string;
  isGroupNameTextValid: boolean;
  doAction: () => void;
  updateState: React.Dispatch<MergeOrUpdateStateAction<CreateGroupDialogState>>;
}

function GroupNameField(props: GroupNameFieldProps): JSX.Element {
  const {
    groupNameErrorMessage,
    groupNameText,
    isGroupNameTextValid,
    doAction
  } = props;

  const onTextFieldClear = () => props.updateState({ groupNameErrorMessage: undefined, groupNameText: '', isGroupNameTextValid: true });
  const onTextFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    const isValid = name.length <= CHARACTER_LIMIT ? true : false;
    props.updateState({
      groupNameErrorMessage: isValid ? undefined : Messages.characterLimitExceeded({
        COUNT_LOC: Formatters.formatCount(name.length),
        LIMIT_LOC: Formatters.formatCount(CHARACTER_LIMIT)
      }),
      groupNameText: name,
      isGroupNameTextValid: isValid,
    });
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      doAction();
    } else if (e.key === 'Escape') {
      onTextFieldClear();
    }
  };

  return <TextFieldWidget
    className={textFieldStyles}
    kind='outline'
    label={Messages.groupName()}
    onChange={onTextFieldChange}
    onClear={onTextFieldClear}
    onKeyDown={onKeyDown}
    message={groupNameErrorMessage ? groupNameErrorMessage : ZERO_WIDTH_SPACE}
    spellCheck={false}
    testId={NAME_GROUP_TEXT_FIELD_TEST_ID}
    valid={isGroupNameTextValid ? undefined : false}
    value={groupNameText}
  />;
}

type GrantLicenseModeOnLoginCheckBoxProps = {
  grantLicenseMode: GrantLicenseModes;
  updateState: React.Dispatch<MergeOrUpdateStateAction<CreateGroupDialogState>>;
}

function GrantLicenseModeOnLoginCheckBox(props: GrantLicenseModeOnLoginCheckBoxProps): JSX.Element {
  const handleCheckboxChange = (checked: boolean) => {
    const grantLicenseMode = checked ? GrantLicenseModes.ON_LOGIN : undefined;
    props.updateState({ grantLicenseMode });
  };

  return <CheckBoxWithLabelWidget
    checked={props.grantLicenseMode === GrantLicenseModes.ON_LOGIN}
    handleChange={handleCheckboxChange}
    testId={GRANT_LICENSE_ON_LOGIN_CHECKBOX_TEST_ID}
    label={Messages.grantLicenseUponLoginCheckboxLabel()}
    disabled={!SessionUserSelectors.canManageUsers(getAppStore().getState())}
  />;
}

type SiteRolePickerMenuWithInfoProps = {
  grantLicenseMode: GrantLicenseModes;
  minimumSiteRole: string;
  updateState: React.Dispatch<MergeOrUpdateStateAction<CreateGroupDialogState>>;
}

function SiteRolePickerMenuWithInfo(props: SiteRolePickerMenuWithInfoProps): JSX.Element {
  const translate = ServiceLocator.get<ng.translate.ITranslateService>('$translate');
  const roleOptions = SiteRoleOptionsResource.instance.getAllSiteRoleOptionsExcept(SiteRoles.ServerAdministrator);

  const disabledRoleOptions = (roleOptions: SiteRoleOption[]) =>
    _.filter(roleOptions, (option: SiteRoleOption) => option.role === SiteRoles.Unlicensed);

  const onSiteRoleSelected = (roleOption: SiteRoleOption) => {
    const minimumSiteRole = roleOption.role;
    const grantLicenseMode = minimumSiteRole === SiteRoles.Unlicensed ? undefined : props.grantLicenseMode;
    props.updateState({ grantLicenseMode, minimumSiteRole });
  };

  const getRoleOption = (siteRole: string) => {
    return SiteRoleOptionsResource.instance.getSiteRoleOptionBySiteRole(siteRole);
  };

  return <SiteRolePickerMenu
    disabledRoleOptions={disabledRoleOptions(roleOptions)}
    dropdownLabel={Messages.minimumSiteRole()}
    getSiteRoleOptionBySiteRole={getRoleOption}
    isDisabled={props.grantLicenseMode !== GrantLicenseModes.ON_LOGIN}
    message={ZERO_WIDTH_SPACE}
    onOptionSelected={onSiteRoleSelected}
    selectedRole={getRoleOption(props.minimumSiteRole)}
    showSiteRolePopover={true}
    siteRoleOptions={roleOptions}
    style={siteRolePickerMenuStyles}
    translate={translate.instant}
  />;
}
